import * as React from 'react'
import { clsx } from 'clsx'

import { ScrollAreaProps } from '@radix-ui/react-scroll-area'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useTabsStore } from '../explore/components/tabs/store'
import { useStorage } from '~/hooks/use-storage'

/**
 * To be used by different tab content components
 */

type TabContentWrapperProps = {
  children?: React.ReactNode
  className?: string
  fillerChildren?: React.ReactNode
  size?: 'xs' | 'sm' | 'md' | 'lg'
  fullHeight?: boolean
  onFillerClick?: () => void
} & Omit<ScrollAreaProps, 'dir'>

export const TabContentDefaultWrapper = ({
  children,
  className,
  fillerChildren,
  size = 'sm',
  fullHeight = false,
  onFillerClick,
  ...rest
}: TabContentWrapperProps) => {
  const [activeTab, revalidateTabsScrollPosition] = useTabsStore((s) => [
    s.activeTab,
    s.revalidateTabsScrollPosition,
  ])
  const [curtainWasTriggered, setCurtainWasTriggered] = useStorage(
    'sessionStorage',
    (z) => z.boolean(),
    `basehub-curtain-triggered`,
    { initialValue: false }
  )
  const [scrollIsRestored, setScrollIsRestored] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!activeTab || !activeTab.scrollTop) {
      setScrollIsRestored(true)
      return
    }

    if (!ref.current) return

    const tabContentScroller = ref.current.childNodes[1]
    if (!(tabContentScroller instanceof HTMLElement)) return

    const timeout = setTimeout(() => {
      if (activeTab.scrollTop) {
        tabContentScroller.scrollTop = activeTab.scrollTop
      }
      setScrollIsRestored(true)
    })

    return () => {
      clearTimeout(timeout)
    }
  }, [activeTab])

  React.useEffect(() => {
    if (!scrollIsRestored) return

    const timeout = setTimeout(() => setCurtainWasTriggered(true), 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [scrollIsRestored, setCurtainWasTriggered])

  React.useLayoutEffect(() => {
    // run revalidateTabsScrollPosition before refreshing the page to save the scroll position
    const beforeunloadAction = () => {
      revalidateTabsScrollPosition()
      setCurtainWasTriggered(false)
    }

    window.addEventListener('beforeunload', beforeunloadAction)

    return () => {
      window.removeEventListener('beforeunload', beforeunloadAction)
    }
  }, [revalidateTabsScrollPosition, setCurtainWasTriggered])

  return (
    <ScrollArea
      {...rest}
      id={tabContentWrapperId}
      ref={ref}
      className={clsx(
        'flex max-h-[var(--editor-height)] flex-grow flex-col bg-dark-base',
        className
      )}
    >
      <div
        className={clsx(
          {
            'px-24 pt-14 max-w-[900px]': size === 'xs',
            'px-24 pt-14 max-w-screen-lg': size === 'sm',
            'px-24 pt-14 max-w-screen-2xl': size === 'md',
            'px-18 pt-14 max-w-screen-3xl': size === 'lg',
          },
          !curtainWasTriggered &&
            'transition-opacity duration-150 delay-100 ease-out',
          scrollIsRestored ? 'opacity-100 scale-100' : 'opacity-0',
          'mx-auto w-full relative z-10',
          fullHeight && 'flex flex-col h-full'
        )}
        id={tabContentId}
      >
        {children}
      </div>

      {fillerChildren && (
        <Filler onClick={onFillerClick}>{fillerChildren}</Filler>
      )}
    </ScrollArea>
  )
}

const Filler = ({
  children,
  onClick,
}: {
  children?: React.ReactNode
  onClick?: () => void
}) => {
  const fillerRef = React.useRef<HTMLDivElement>(null)
  const [fillerHeight, setFillerHeight] = React.useState(218)

  React.useEffect(() => {
    // set up a mutation observer to calculate filler's height based on what's left between the #scroll-area and #editor-content:
    const scrollArea = document.querySelector<HTMLDivElement>(
      `#${tabContentWrapperId}`
    )
    const editorContent = document.querySelector<HTMLDivElement>(
      `#${tabContentId}`
    )

    if (!scrollArea || !editorContent) return

    const observer = new MutationObserver(() => {
      const scrollAreaHeight = scrollArea.getBoundingClientRect().height
      const editorContentHeight = editorContent.getBoundingClientRect().height

      const filler = fillerRef.current

      if (!filler) return

      const minHeight = 218

      const newFillerHeight = Math.max(
        minHeight,
        scrollAreaHeight - editorContentHeight - 12
      )

      setFillerHeight(newFillerHeight)
    })

    observer.observe(scrollArea, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div
      className="relative flex-shrink-0 flex-grow cursor-default"
      onClick={onClick}
      ref={fillerRef}
      style={{ height: fillerHeight }}
      id="editor-filler"
    >
      {children}
    </div>
  )
}

export const tabContentWrapperId = 'tab-content-wrapper'
export const tabContentId = 'tab-content'
